import React, { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Table, Alert, Form, Badge, Button, Row, Col } from 'react-bootstrap'
import { useAdminUsers } from 'api/hooks/use-admin-user'
import { useAdminMunicipalityOptions } from 'api/hooks/use-admin-municipalities'
import Search from 'shared/forms/search'
import TableFooter from 'shared/tables/table-footer'
import TableLoading from 'shared/tables/table-loading'
import SortArrow from 'shared/tables/sort-arrow'
import { updateOrdering } from 'shared/tables/utils'
import AppBadge from 'shared/badges/app-badge'
import UserStatusBadge from 'shared/badges/user-status-badge'
import { formatDate, formatTimeAgo } from './user-activity-format'

const COLUMNS = 9

// Fallback only — the real window comes back on every row as
// login_stats.window_days, so the column header follows the backend.
const DEFAULT_WINDOW_DAYS = 30

const APP_OPTIONS = [
  { value: 'analytics', label: 'Analytics' },
  { value: 'sod', label: 'Access Management' },
  { value: 'datum', label: 'Reports (Datum)' }
]

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'scheduled_block', label: 'Auto-block scheduled' }
]

const ACTIVITY_OPTIONS = [
  { value: 'active', label: 'Signed in this month' },
  { value: 'dormant', label: 'Dormant (no logins this month)' },
  { value: 'never', label: 'Never signed in' }
]

const USER_TYPE_OPTIONS = [
  { value: 'admin', label: 'ThirdLine admins' },
  { value: 'user_admin', label: 'Municipal user admins' },
  { value: 'standard', label: 'Standard users' }
]

const AUDIENCE_OPTIONS = [
  { value: 'external', label: 'Customers only' },
  { value: 'internal', label: 'ThirdLine staff only' }
]

const INITIAL_PARAMS = {
  page: 1,
  perPage: 25,
  orderBy: 'last_login_at',
  orderDirection: 'desc',
  search: '',
  municipality_id: '',
  app: '',
  status: '',
  activity: '',
  user_type: '',
  audience: '',
  min_total_logins: '',
  min_recent_logins: ''
}

const UserAppBadges = ({ user }) => {
  const apps = [
    user.has_analytics_app && 'Analytics',
    user.has_sod_app && 'Access Mgmt',
    user.has_datum_app && 'Datum'
  ].filter(Boolean)

  if (apps.length === 0) return <span className="text-muted">--</span>

  return (
    <div className="d-flex flex-wrap gap-1">
      {apps.map((app) => (
        <Badge key={app} bg="light" text="primary">{app}</Badge>
      ))}
    </div>
  )
}

const UsersList = () => {
  const [params, setParamsState] = useState(INITIAL_PARAMS)

  const setParams = useCallback((newParams) => {
    setParamsState((prevParams) =>
      typeof newParams === 'function' ? newParams(prevParams) : newParams
    )
  }, [])

  const { data: response, isLoading, isSuccess, error } = useAdminUsers({ params })
  const { data: municipalities } = useAdminMunicipalityOptions()

  const users = response?.data
  const meta = response?.meta
  const windowDays = users?.[0]?.login_stats?.window_days ?? DEFAULT_WINDOW_DAYS

  const handleFilter = (key) => (e) => {
    // Read the value now: the updater runs after React has restored the controlled
    // input to its previous value, so a lazy `e.target.value` would come back stale.
    const { value } = e.target
    setParams((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handleSort = (column) => {
    updateOrdering(column, params, setParams)
  }

  const clearFilters = () => {
    setParams({ ...INITIAL_PARAMS, perPage: params.perPage })
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error loading users</Alert.Heading>
        <p>Error message: {error.message}</p>
      </Alert>
    )
  }

  const sortProps = { orderBy: params.orderBy, orderDirection: params.orderDirection }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Users</h2>
        <Button variant="light" className="text-primary" onClick={clearFilters}>
          Clear Filters
        </Button>
      </div>

      {/* Debounces and resets the page itself — this table can hold every user
          in the product, so it must not query per keystroke. */}
      <div className="mb-3">
        <Search
          params={params}
          setParams={setParams}
          placeholder="Search by name, email, or municipality..."
        />
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Municipality</Form.Label>
            <Form.Select value={params.municipality_id} onChange={handleFilter('municipality_id')}>
              <option value="">All municipalities</option>
              {municipalities?.map((municipality) => (
                <option key={municipality.id} value={municipality.id}>{municipality.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Application</Form.Label>
            <Form.Select value={params.app} onChange={handleFilter('app')}>
              <option value="">All applications</option>
              {APP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Status</Form.Label>
            <Form.Select value={params.status} onChange={handleFilter('status')}>
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Login Activity</Form.Label>
            <Form.Select value={params.activity} onChange={handleFilter('activity')}>
              <option value="">Any activity</option>
              {ACTIVITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">User Type</Form.Label>
            <Form.Select value={params.user_type} onChange={handleFilter('user_type')}>
              <option value="">All user types</option>
              {USER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Audience</Form.Label>
            <Form.Select value={params.audience} onChange={handleFilter('audience')}>
              <option value="">Everyone</option>
              {AUDIENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Min Total Logins</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="Any"
              value={params.min_total_logins}
              onChange={handleFilter('min_total_logins')}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">{`Min Logins (${windowDays}d)`}</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="Any"
              value={params.min_recent_logins}
              onChange={handleFilter('min_recent_logins')}
            />
          </Form.Group>
        </Col>
      </Row>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th role="button" onClick={() => handleSort('last_name')}>
              Name <SortArrow column="last_name" {...sortProps} />
            </th>
            <th role="button" onClick={() => handleSort('email')}>
              Email <SortArrow column="email" {...sortProps} />
            </th>
            <th role="button" onClick={() => handleSort('municipality_name')}>
              Municipality <SortArrow column="municipality_name" {...sortProps} />
            </th>
            <th>Applications</th>
            <th role="button" onClick={() => handleSort('total_logins')}>
              Total Logins <SortArrow column="total_logins" {...sortProps} />
            </th>
            <th role="button" onClick={() => handleSort('recent_logins')}>
              {`Logins (${windowDays}d) `}
              <SortArrow column="recent_logins" {...sortProps} />
            </th>
            <th role="button" onClick={() => handleSort('last_login_at')}>
              Last Login <SortArrow column="last_login_at" {...sortProps} />
            </th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isSuccess && users?.map((user) => (
            <tr key={user.id}>
              <td>
                <Link to={`/admin/users/${user.id}`}>{user.full_name}</Link>
                {user.is_admin && <AppBadge variant="danger" className="ms-2">Admin</AppBadge>}
                {user.is_user_admin && <Badge bg="light" text="primary" className="ms-2">User Admin</Badge>}
              </td>
              <td>{user.email}</td>
              <td>
                <Link to={`/admin/municipalities/${user.municipality.id}`}>
                  {user.municipality.name}
                </Link>
              </td>
              <td><UserAppBadges user={user} /></td>
              <td>{user.login_stats.total_logins.toLocaleString()}</td>
              <td>{user.login_stats.recent_logins.toLocaleString()}</td>
              <td>
                {user.login_stats.last_login_at ? (
                  <>
                    {formatDate(user.login_stats.last_login_at)}
                    <div className="small text-muted">
                      {formatTimeAgo(user.login_stats.last_login_at)}
                    </div>
                  </>
                ) : (
                  <span className="text-muted">Never</span>
                )}
              </td>
              <td>
                {/* Mirrored `blocked` flag, not a live Auth0 lookup — the detail
                    page resolves the real Auth0 state for one user at a time. */}
                <UserStatusBadge state={user.blocked ? 'blocked' : 'active'} />
                {!user.blocked && user.block_scheduled_for && (
                  <div className="small text-muted mt-1">
                    Auto-block {formatDate(`${user.block_scheduled_for}T00:00:00`)}
                  </div>
                )}
              </td>
              <td>
                <Button as={Link} to={`/admin/users/${user.id}`} variant="info" size="sm">
                  View
                </Button>
              </td>
            </tr>
          ))}
          {isLoading && <TableLoading columns={COLUMNS} />}
        </tbody>
      </Table>

      {isSuccess && meta && (
        <TableFooter meta={meta} params={params} setParams={setParams} />
      )}

      {isSuccess && users?.length === 0 && (
        <Alert variant="info">No users found.</Alert>
      )}
    </div>
  )
}

export default UsersList
